"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Link from "next/link";
import toast from "react-hot-toast";
import { motion } from "framer-motion";
import { Lock, User, Mail, ArrowLeft, Eye, EyeOff } from "lucide-react";

import { signUpSchema, type SignUpFormData } from "../../../lib/schemas";
import { signUp, checkUsernameAvailability } from "../../../lib/auth";
import {
  loginWithKakao,
  loginWithNaver,
  handleRedirectResult,
} from "../../../lib/auth/snsAuth";
import { signIn as nextAuthSignIn } from "next-auth/react";
import { Button } from "../../../components/ui/Button";
import { Select } from "../../../components/ui/Select";
import { TermsModal } from "../../../components/auth/TermsModal";
import { KOREAN_REGIONS } from "../../../lib/utils";
import {
  getPasswordStrength,
} from "../../../lib/utils/passwordValidation";
import SnsAuthButtons, {
  type SnsProvider,
} from "@/components/auth/SnsAuthButtons";
import { guardGoogleOAuthInAppBrowser } from "@/components/auth/InAppBrowserGoogleBanner";
import {
  scheduleSnsLoadingFallback,
  useSnsOAuthLoadingRecovery,
} from "@/lib/hooks/useSnsOAuthLoadingRecovery";

export default function SignUpPage() {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);
  const [usernameChecked, setUsernameChecked] = useState(false);
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(
    null
  );
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [usernameError, setUsernameError] = useState("");
  const [snsLoading, setSnsLoading] = useState<SnsProvider>(null);

  useSnsOAuthLoadingRecovery(setSnsLoading);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsModalType, setTermsModalType] = useState<"service" | "privacy">(
    "service"
  );

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<SignUpFormData>({
    resolver: zodResolver(signUpSchema),
  });

  const password = watch("password", "");
  const username = watch("username", "");

  // 리다이렉트 로그인 결과 확인 (모바일에서 redirect 방식 사용 시)
  useEffect(() => {
    // Hydration 직후 즉시 redirect가 발생하면 초기 DOM과 달라져
    // hydration mismatch가 날 수 있어, 최소 지연 후에 처리합니다.
    let cancelled = false;
    const timerId = window.setTimeout(() => {
      const checkRedirectResult = async () => {
        try {
          const result = await handleRedirectResult();
          if (!cancelled && result) {
            router.push("/");
          }
        } catch (error) {
          // 리다이렉트 결과가 없거나 오류인 경우 무시 (정상적인 경우)
          console.log("리다이렉트 결과 확인:", error);
        }
      };

      checkRedirectResult();
    }, 0);

    return () => {
      cancelled = true;
      window.clearTimeout(timerId);
    };
  }, [router]);

  const handleCheckUsername = async () => {
    if (!username || username.length < 4) {
      toast.error("아이디를 4자 이상 입력해주세요.");
      return;
    }

    setIsCheckingUsername(true);
    try {
      const isAvailable = await checkUsernameAvailability(username);
      setUsernameAvailable(isAvailable);
      setUsernameChecked(true);

      if (isAvailable) {
        toast.success("사용 가능한 아이디입니다!");
      } else {
        toast.error("이미 사용 중인 아이디입니다.");
      }
    } catch (error) {
      toast.error("아이디 중복확인 중 오류가 발생했습니다.");
      setUsernameAvailable(null);
      setUsernameChecked(false);
    } finally {
      setIsCheckingUsername(false);
    }
  };

  const onSubmit = async (data: SignUpFormData) => {
    // 아이디 중복확인 체크
    if (!usernameChecked || !usernameAvailable) {
      toast.error("아이디 중복확인을 완료해주세요.");
      return;
    }
    setIsLoading(true);

    try {
      await signUp({
        username: data.username,
        email: data.email,
        password: data.password,
        nickname: data.nickname,
        region: data.region,
        userType: data.userType,
        agreeTerms: data.agreeTerms,
      });

      toast.success("회원가입이 완료되었습니다!");
      toast.success("🎁 웰컴 포인트 5,000P가 적립되었습니다!");
      
      // 가입 유형에 따라 리다이렉트
      if (data.userType === "instructor") {
        router.push("/instructor/register");
      } else {
        router.push("/onboarding");
      }
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "회원가입 중 오류가 발생했습니다."
      );
    } finally {
      setIsLoading(false);
    }
  };

  // SNS 로그인 핸들러
  const handleSNSLogin = async (provider: "google" | "kakao" | "naver") => {
    if (provider === "google" && (await guardGoogleOAuthInAppBrowser())) {
      return;
    }

    setSnsLoading(provider);
    const clearSnsLoading = () => setSnsLoading(null);
    const cancelFallback = scheduleSnsLoadingFallback(clearSnsLoading);

    try {
      if (provider === "google") {
        await nextAuthSignIn("google", {
          callbackUrl: "/onboarding?next=/",
          redirect: true,
        });
        return;
      }

      if (provider === "kakao") {
        await loginWithKakao("/onboarding?next=/");
        return;
      }

      if (provider === "naver") {
        await loginWithNaver("/onboarding?next=/");
        return;
      }
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : `${provider === "google" ? "구글" : provider === "kakao" ? "카카오" : "네이버"} 회원가입 중 오류가 발생했습니다.`
      );
    } finally {
      cancelFallback();
      clearSnsLoading();
    }
  };

  return (
    <div className="min-h-screen bg-[#f7f8fa]">
      <div className="bg-white border-b border-gray-200">
        <div className="px-4 py-4 flex items-center max-w-md mx-auto">
          <button
            onClick={() => router.back()}
            className="mr-4 p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-700" />
          </button>
          <h1 className="text-lg font-semibold text-gray-900">회원가입</h1>
        </div>
      </div>

      <div className="px-4 py-8 min-h-[calc(100vh-73px)] flex items-center">
        <div className="max-w-md w-full mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 sm:p-8"
          >
            <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
              {/* 구글은 한눈에 보이도록 상단 배치 (기존에는 폼 하단이라 스크롤해야 보였음) */}
              <div className="space-y-4">
                <SnsAuthButtons
                  mode="signup"
                  layout="icons"
                  snsLoading={snsLoading}
                  onGoogle={() => handleSNSLogin("google")}
                  onKakao={() => handleSNSLogin("kakao")}
                  onNaver={() => handleSNSLogin("naver")}
                />
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300/50" />
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="px-3 bg-white/80 text-gray-500">
                      또는 이메일로 가입
                    </span>
                  </div>
                </div>
              </div>

              <div className="space-y-5">
                {/* 아이디 입력 필드 */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    아이디 <span className="text-red-500">*</span>
                  </label>
                  <div className="flex space-x-2">
                    <div className="flex-1 relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <User className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        autoComplete="username"
                        placeholder="4-20자, 영어와 숫자만 사용 가능"
                        className={`w-full pl-12 pr-4 py-3.5 bg-gray-50 border rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all ${
                          errors.username ? "border-red-500" : "border-gray-200"
                        } ${
                          usernameChecked && usernameAvailable
                            ? "border-green-500"
                            : ""
                        } ${
                          usernameChecked && !usernameAvailable
                            ? "border-red-500"
                            : ""
                        }`}
                        {...register("username")}
                        onChange={e => {
                          register("username").onChange(e);
                          setUsernameChecked(false);
                          setUsernameAvailable(null);
                        }}
                        onKeyDown={e => {
                          if (
                            e.key === "Process" ||
                            /[ㄱ-ㅎ|ㅏ-ㅣ|가-힣]/.test(e.key)
                          ) {
                            e.preventDefault();
                            setUsernameError(
                              "아이디는 영어와 숫자만 사용할 수 있습니다."
                            );
                            setTimeout(() => setUsernameError(""), 3000);
                          }
                        }}
                        onInput={e => {
                          const target = e.target as HTMLInputElement;
                          const value = target.value.replace(/[^a-zA-Z0-9]/g, "");
                          if (target.value !== value) {
                            target.value = value;
                            register("username").onChange({ target });
                            setUsernameError(
                              "아이디는 영어와 숫자만 사용할 수 있습니다."
                            );
                            setTimeout(() => setUsernameError(""), 3000);
                          }
                        }}
                      />
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleCheckUsername}
                      loading={isCheckingUsername}
                      disabled={
                        !username || username.length < 4 || isCheckingUsername
                      }
                      className="px-4 py-3.5 whitespace-nowrap rounded-xl"
                    >
                      {isCheckingUsername ? "확인중..." : "중복확인"}
                    </Button>
                  </div>
                  {errors.username && (
                    <p className="mt-2 text-sm text-red-500 flex items-center gap-1">
                      <span>⚠️</span>
                      {errors.username.message}
                    </p>
                  )}
                  {usernameError && (
                    <p className="mt-2 text-sm text-red-500">{usernameError}</p>
                  )}
                  {usernameChecked && usernameAvailable && (
                    <p className="mt-2 text-sm text-green-600">
                      ✓ 사용 가능한 아이디입니다
                    </p>
                  )}
                  {usernameChecked && !usernameAvailable && (
                    <p className="mt-2 text-sm text-red-600">
                      ✗ 이미 사용 중인 아이디입니다
                    </p>
                  )}
                  {!usernameChecked && !usernameError && (
                    <p className="mt-2 text-sm text-gray-500">
                      영어(대소문자)와 숫자만 사용할 수 있습니다
                    </p>
                  )}
                </div>

                {/* 가입 유형 선택 */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    가입 유형 <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        // userType을 student로 설정하는 로직
                        const event = {
                          target: { name: "userType", value: "student" }
                        } as any;
                        register("userType").onChange(event);
                      }}
                      className={`p-4 rounded-xl border-2 transition-all text-center ${
                        watch("userType") === "student"
                          ? "border-purple-500 bg-purple-50 shadow-lg"
                          : "border-gray-200 hover:border-gray-300 bg-white"
                      }`}
                    >
                      <div className="text-2xl mb-2">🎵</div>
                      <div className={`text-sm font-medium transition-colors ${
                        watch("userType") === "student"
                          ? "text-purple-700"
                          : "text-gray-700"
                      }`}>
                        일반 회원
                      </div>
                      <div className={`text-xs mt-1 transition-colors ${
                        watch("userType") === "student"
                          ? "text-purple-600"
                          : "text-gray-500"
                      }`}>
                        피드백 받고 싶어요
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        // userType을 instructor로 설정하는 로직
                        const event = {
                          target: { name: "userType", value: "instructor" }
                        } as any;
                        register("userType").onChange(event);
                      }}
                      className={`p-4 rounded-xl border-2 transition-all text-center ${
                        watch("userType") === "instructor"
                          ? "border-purple-500 bg-purple-50 shadow-lg"
                          : "border-gray-200 hover:border-gray-300 bg-white"
                      }`}
                    >
                      <div className="text-2xl mb-2">🎼</div>
                      <div className={`text-sm font-medium transition-colors ${
                        watch("userType") === "instructor"
                          ? "text-purple-700"
                          : "text-gray-700"
                      }`}>
                        프로 강사
                      </div>
                      <div className={`text-xs mt-1 transition-colors ${
                        watch("userType") === "instructor"
                          ? "text-purple-600"
                          : "text-gray-500"
                      }`}>
                        피드백 제공할게요
                      </div>
                    </button>
                  </div>
                  {errors.userType && (
                    <p className="mt-2 text-sm text-red-500 flex items-center gap-1">
                      <span>⚠️</span>
                      {errors.userType.message}
                    </p>
                  )}
                </div>

                {/* 비밀번호 입력 필드 */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    비밀번호 <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type={showPassword ? "text" : "password"}
                      autoComplete="new-password"
                      placeholder="8자 이상, 영문+숫자+특수문자 포함"
                      className={`w-full pl-12 pr-12 py-3.5 bg-gray-50 border rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all ${
                        errors.password ? "border-red-500" : "border-gray-200"
                      }`}
                      {...register("password")}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="mt-2 text-sm text-red-500 flex items-center gap-1">
                      <span>⚠️</span>
                      {errors.password.message}
                    </p>
                  )}
                  {password && (
                    <div className="mt-2">
                      <div className="flex items-center space-x-2">
                        <div className="flex-1 bg-gray-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full transition-all duration-300 ${
                              getPasswordStrength(password).score === 0
                                ? "bg-red-500 w-0"
                                : getPasswordStrength(password).score === 1
                                  ? "bg-red-500 w-1/4"
                                  : getPasswordStrength(password).score === 2
                                    ? "bg-orange-500 w-2/4"
                                    : getPasswordStrength(password).score === 3
                                      ? "bg-yellow-500 w-3/4"
                                      : getPasswordStrength(password).score === 4
                                        ? "bg-blue-500 w-full"
                                        : "bg-green-500 w-full"
                            }`}
                          />
                        </div>
                        <span
                          className={`text-sm font-medium ${getPasswordStrength(password).color}`}
                        >
                          {getPasswordStrength(password).label}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* 비밀번호 확인 입력 필드 */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    비밀번호 확인 <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      autoComplete="new-password"
                      placeholder="비밀번호를 다시 입력해주세요"
                      className={`w-full pl-12 pr-12 py-3.5 bg-gray-50 border rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all ${
                        errors.confirmPassword
                          ? "border-red-500"
                          : "border-gray-200"
                      }`}
                      {...register("confirmPassword")}
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <p className="mt-2 text-sm text-red-500 flex items-center gap-1">
                      <span>⚠️</span>
                      {errors.confirmPassword.message}
                    </p>
                  )}
                </div>

                {/* 이메일 입력 필드 */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    이메일 <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="email"
                      autoComplete="email"
                      placeholder="이메일을 입력해주세요"
                      className={`w-full pl-12 pr-4 py-3.5 bg-gray-50 border rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all ${
                        errors.email ? "border-red-500" : "border-gray-200"
                      }`}
                      {...register("email")}
                    />
                  </div>
                  {errors.email && (
                    <p className="mt-2 text-sm text-red-500 flex items-center gap-1">
                      <span>⚠️</span>
                      {errors.email.message}
                    </p>
                  )}
                  <p className="mt-1 text-sm text-gray-500">
                    비밀번호 찾기 시 사용됩니다
                  </p>
                </div>

                {/* 닉네임 입력 필드 */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    닉네임 <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <User className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      autoComplete="nickname"
                      placeholder="닉네임을 입력해주세요"
                      className={`w-full pl-12 pr-4 py-3.5 bg-gray-50 border rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all ${
                        errors.nickname ? "border-red-500" : "border-gray-200"
                      }`}
                      {...register("nickname")}
                    />
                  </div>
                  {errors.nickname && (
                    <p className="mt-2 text-sm text-red-500 flex items-center gap-1">
                      <span>⚠️</span>
                      {errors.nickname.message}
                    </p>
                  )}
                  <p className="mt-1 text-sm text-gray-500">
                    한글, 영문, 숫자만 사용 가능합니다
                  </p>
                </div>

                {/* 지역 선택 */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    지역
                  </label>
                  <Select
                    placeholder="지역을 선택해주세요"
                    options={KOREAN_REGIONS}
                    error={errors.region?.message}
                    {...register("region")}
                  />
                </div>

                {/* 약관 동의 */}
                <div className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      id="agreeTerms"
                      className="h-4 w-4 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500 focus:ring-2 mt-0.5"
                      {...register("agreeTerms")}
                    />
                    <label
                      htmlFor="agreeTerms"
                      className="text-sm text-gray-700 cursor-pointer"
                    >
                      <span
                        onClick={() => {
                          setTermsModalType("service");
                          setShowTermsModal(true);
                        }}
                        className="text-purple-600 hover:text-purple-800 underline cursor-pointer"
                      >
                        서비스 이용약관
                      </span>
                      및
                      <span
                        onClick={() => {
                          setTermsModalType("privacy");
                          setShowTermsModal(true);
                        }}
                        className="text-purple-600 hover:text-purple-800 underline cursor-pointer ml-1"
                      >
                        개인정보처리방침
                      </span>
                      에 동의합니다
                    </label>
                  </div>
                  {errors.agreeTerms && (
                    <p className="text-sm text-red-500">
                      {errors.agreeTerms.message}
                    </p>
                  )}
                </div>
              </div>

              {/* 회원가입 버튼 */}
              <Button
                type="submit"
                className="w-full bg-violet-600 hover:bg-violet-700 text-white font-semibold py-3.5 rounded-xl transition-colors"
                loading={isLoading}
                disabled={isLoading}
              >
                {isLoading ? "회원가입 중..." : "회원가입"}
              </Button>

              {/* 미니 배너 */}
              <div className="bg-violet-50 border border-violet-100 rounded-xl p-3 text-center">
                <p className="text-sm text-purple-800">
                  회원가입하면 <strong>5,000P</strong> 지급!
                </p>
              </div>

              <div className="text-center">
                <p className="text-sm text-gray-600">
                  이미 계정이 있으신가요?{" "}
                  <Link
                    href="/auth/login"
                    className="font-medium text-purple-600 hover:text-purple-500"
                  >
                    로그인하기
                  </Link>
                </p>
              </div>
            </form>
          </motion.div>
        </div>
      </div>

      {/* 약관 모달 */}
      <TermsModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        type={termsModalType}
      />

      {/* reCAPTCHA 컨테이너 */}
      <div id="recaptcha-container"></div>
    </div>
  );
}
